'use client'

import React, { Suspense, useState, useEffect } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { signIn, useSession } from 'next-auth/react'
import { Button } from '@/app/components/common/Button'
import { Input } from '@/app/components/common/Input'
import { Card } from '@/app/components/common/Card'
import { Header } from '@/app/components/common/Header'
import { logError } from '@/lib/logging'

function LoginForm() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const { data: session, status } = useSession()
  const callbackUrl = searchParams.get('callbackUrl') || searchParams.get('redirectTo')

  // Show message if user was redirected from a protected page
  const isRedirected = searchParams.has('callbackUrl') || searchParams.has('redirectTo')

  // Handle redirect after successful login based on user role
  useEffect(() => {
    if (status === 'authenticated' && session?.user) {
      // If there's a callback URL, use it
      if (callbackUrl) {
        router.push(callbackUrl)
        return
      }

      // Check if user is super user first
      if ((session.user as any).isSuper) {
        router.push('/ops')
        return
      }

      // Determine where to redirect based on user's role
      const currentOrgId = session.user.currentOrgId
      const currentOrg = session.user.orgs?.find((org) => org.id === currentOrgId)
      const primaryRole = currentOrg?.role

      // Otherwise redirect based on role
      if (primaryRole === 'client') {
        router.push('/dashboard')
      } else if (primaryRole === 'admin' || primaryRole === 'provider') {
        router.push('/admin')
      } else {
        // Fallback
        router.push('/dashboard')
      }
    }
  }, [status, session, router, callbackUrl])

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')

    if (!email) {
      setError('Email is required')
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send code')
      }

      // Redirect to code verification page with callback URL
      const returnTo = callbackUrl || '/dashboard'
      router.push(
        `/auth/verify-code?email=${encodeURIComponent(email)}&returnTo=${encodeURIComponent(returnTo)}`
      )
    } catch (err) {
      logError('Failed to send code', err)
      setError(err instanceof Error ? err.message : 'Failed to send code')
      setIsSubmitting(false)
    }
  }

  return (
    <div
      data-component-name="LoginForm"
      className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4"
    >
      <div className="max-w-md mx-auto w-full">
        <div className="text-center mb-8">
          <Header level={1} centered>
            Welcome Back
          </Header>
          <p className="mt-2 text-gray-600">Enter your email to receive a login code</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            {isRedirected && !error && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
                Please sign in to access this page
              </div>
            )}

            {error && (
              <div className="bg-danger/10 border border-danger text-danger px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              fullWidth
              autoComplete="email"
              autoFocus
            />

            <Button type="submit" fullWidth disabled={isSubmitting}>
              {isSubmitting ? 'Sending code...' : 'Continue'}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={() => signIn('google', { callbackUrl: callbackUrl || undefined })}
                disabled={isSubmitting}
                aria-label="Sign in with Google"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
              </Button>
              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={() => signIn('yahoo', { callbackUrl: callbackUrl || undefined })}
                disabled={isSubmitting}
                aria-label="Sign in with Yahoo"
              >
                <svg className="w-5 h-5" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill="#5F01D1"
                    d="M223.69 141.06L167 284.23l-56-143.17H14.93l105.83 249.13L82.19 480h94.17l140.91-338.94Zm105.4 135.79a58.22 58.22 0 1 0 58.22 58.22a58.22 58.22 0 0 0-58.22-58.22M394.65 32l-93 223.47h104.79L499.07 32Z"
                  />
                </svg>
              </Button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              New to Okkanti?{' '}
              <span className="text-gray-900 font-medium">
                Just enter your email to get started!
              </span>
            </p>
          </div>

          <div className="mt-4 text-center text-xs text-gray-500">
            By continuing, you agree to our{' '}
            <a href="/terms" target="_blank" className="text-primary hover:underline">
              Terms & Conditions
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" className="text-primary hover:underline">
              Privacy Policy
            </a>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4">
          <div className="max-w-md mx-auto w-full">
            <Card>
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-1/2 mx-auto mb-4"></div>
                <div className="space-y-4">
                  <div className="h-10 bg-gray-200 rounded"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  )
}
